import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useParams, useSearchParams } from "react-router-dom";
import { convertCurrency, getSumission } from "src/utils/helper/functions";
import { RequestsType, Status } from "src/models/enum";
import DashboardContext from "./dashboard-context";
import instance from "src/store/axios";

const DashboardProvider = ({ children }: any) => {
  const { accountId, activeKyc } = useParams();
  const user = useSelector((state: any) => state?.user);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [assets, setAssets] = useState<any>([]);
  const [liabilities, setLiabilities] = useState<any>([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [liabilitiesTotal, setLiabilitiesTotal] = useState(0);
  const [netWorth, setNetWorth] = useState(0);
  const [portfolioList, setPortfolioList] = useState<any>([]);
  const [selectedPortfolio, setSelectedPortfolio] = useState<any>(null);
  const [fxRates, setFxRates] = useState<any>([]);
  const [pendingSubscriptionRequests, setPendingSubscriptionRequests] =
    useState<any>([]);
  const [availableCash, setAvailableCash] = useState(0);
  const [pendingDepositCash, setPendingDepositCash] = useState(0);
  const [pendingRedemptionCash, setPendingRedemptionCash] = useState(0);
  const [pendingWithdrawCash, setPendingWithdrawCash] = useState(0);
  const [pendingSubscriptionCash, setPendingSubscriptionCash] = useState(0);
  const [sumOfPendingRequestsCash, setSumOfPendingRequestsCash] = useState(0);
  const [sumOfPendingCash, setSumOfPendingCash] = useState(0);
  const [totalPayableCash, setTotalPayableCash] = useState(0);
  const [selectedAccountTotalAssets, setSelectedAccountTotalAssets] =
    useState<any>(null);
  const [selectedAccountTotalLiabilities, setSelectedAccountTotalLiabilities] =
    useState<any>(null);
  const [selectedAccountNetWorth, setSelectedAccountNetWorth] =
    useState<any>(null);

  useEffect(() => {
    if (user && accountId) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const fxRatesResponse = await instance.get(
            `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/fxrates`
          );
          setFxRates(fxRatesResponse?.data?.data);

          let accountDataResponse = await await instance.get(
            `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/dashboard/${accountId}`
          );
          setSelectedAccount(accountDataResponse?.data?.data);

          setLoading(false);
        } catch (error: any) {
          if (error?.response?.status !== 401) {
            toast.error(error?.response?.data?.message || "An error occurred");
          }
          localStorage.setItem(
            "star_prev_url",
            `/home/business-account/${accountId}/${activeKyc}/details`
          );
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [user, accountId]);

  useEffect(() => {
    if (!selectedAccount) return;

    if (!selectedPortfolio) {
      setPortfolioList(selectedAccount?.portfolios);
      setSelectedPortfolio(null);
    }
    setTransactions(selectedAccount?.transactions);
    setAssets(
      selectedAccount?.holdings?.filter(
        (item: any) => item.assetLiabilityCode === "A"
      )
    );
    setSelectedAccountTotalAssets(
      getArraySumission(
        selectedAccount?.holdings?.filter(
          (item: any) => item.assetLiabilityCode === "A"
        )
      )
    );
    setLiabilities(
      selectedAccount?.holdings?.filter(
        (item: any) => item.assetLiabilityCode === "L"
      )
    );
    setSelectedAccountTotalLiabilities(
      getArraySumission(
        selectedAccount?.holdings?.filter(
          (item: any) => item.assetLiabilityCode === "L"
        )
      )
    );
  }, [selectedAccount]);

  useEffect(() => {
    setSelectedAccountNetWorth(
      selectedAccountTotalAssets - Math.abs(selectedAccountTotalLiabilities)
    );
  }, [selectedAccountTotalAssets, selectedAccountTotalLiabilities]);

  useEffect(() => {
    if (pendingSubscriptionRequests?.length > 0) {
      setSumOfPendingRequestsCash(
        getSumission(
          pendingSubscriptionRequests?.filter(
            (request: any) => request.type === "SUBSCRIPTION"
          ),
          "selectedCashEquv"
        ) +
          getSumission(
            pendingSubscriptionRequests?.filter(
              (request: any) => request.type === "WITHDRAW"
            ),
            "amount",
            selectedPortfolio?.reportingCurrency ||
              selectedAccount?.reportingCurrency,
            fxRates
          )
      );
    } else {
      setSumOfPendingRequestsCash(0);
    }
  }, [pendingSubscriptionRequests]);

  useEffect(() => {
    setTotalPayableCash(
      getSumission(
        liabilities.filter?.((item: any) => item.productGroupCode == "CSH"),
        "reportingMarketAmount",
        selectedPortfolio?.reportingCurrency,
        fxRates
      ) -
        Math.abs(
          getSumission(
            assets.filter?.((item: any) => item.productGroupCode == "CSH"),
            "reportingPendingAmount",
            selectedPortfolio?.reportingCurrency,
            fxRates
          )
        ) -
        Math.abs(sumOfPendingRequestsCash)
    );

    setAvailableCash(
      getSumission(
        assets.filter?.((item: any) => item.productGroupCode == "CSH"),
        "reportingMarketAmount",
        selectedPortfolio?.reportingCurrency,
        fxRates
      ) -
        Math.abs(
          getSumission(
            assets.filter?.((item: any) => item.productGroupCode == "CSH"),
            "reportingPendingAmount",
            selectedPortfolio?.reportingCurrency,
            fxRates
          )
        ) -
        Math.abs(sumOfPendingRequestsCash)
    );

    setSumOfPendingCash(
      Math.abs(
        getSumission(
          assets.filter?.((item: any) => item.productGroupCode == "CSH"),
          "reportingPendingAmount",
          selectedPortfolio?.reportingCurrency,
          fxRates
        )
      ) + Math.abs(sumOfPendingRequestsCash)
    );
  }, [sumOfPendingRequestsCash, assets]);

  useEffect(() => {
    if (selectedPortfolio && selectedAccount) {
      setPendingDepositCash(
        selectedAccount?.requests?.reduce((sum: any, item: any) => {
          if (
            item.portfolioCode == selectedPortfolio?.accountCode &&
            item.type === "DEPOSIT" &&
            item.status.toLowerCase() === "pending"
          ) {
            return (
              sum +
              convertCurrency(
                item.amount,
                item?.currency,
                selectedPortfolio?.reportingCurrency,
                fxRates
              )
            );
          }
          return sum;
        }, 0)
      );

      setPendingRedemptionCash(
        selectedAccount?.requests?.reduce((sum: any, item: any) => {
          if (
            item.portfolioCode == selectedPortfolio?.accountCode &&
            item.type === "REDEMPTION" &&
            item.status.toLowerCase() === "pending"
          ) {
            return sum + item.redemptionUnit;
          }
          return sum;
        }, 0)
      );

      setPendingWithdrawCash(
        selectedAccount?.requests?.reduce((sum: any, item: any) => {
          if (
            item.portfolioCode == selectedPortfolio?.accountCode &&
            item.type === "WITHDRAW" &&
            item.status.toLowerCase() === "pending"
          ) {
            return (
              sum +
              convertCurrency(
                item.amount,
                item?.currency,
                selectedPortfolio?.reportingCurrency,
                fxRates
              )
            );
          }
          return sum;
        }, 0)
      );

      setPendingSubscriptionCash(
        selectedAccount?.requests?.reduce((sum: any, item: any) => {
          if (
            item.portfolioCode == selectedPortfolio?.accountCode &&
            item.type === "SUBSCRIPTION" &&
            item.status.toLowerCase() === "pending"
          ) {
            return (
              sum +
              convertCurrency(
                item.amount,
                item?.currency,
                selectedPortfolio?.reportingCurrency,
                fxRates
              )
            );
          }
          return sum;
        }, 0)
      );

      setPendingSubscriptionRequests(
        selectedAccount?.requests?.filter(
          (item: any) =>
            (item.type == RequestsType.SUBSCRIPTION ||
              item.type == RequestsType.WITHDRAW) &&
            item.status?.toLowerCase() == Status.Pending &&
            item.portfolioCode == selectedPortfolio?.accountCode
        )
      );

      setTransactions(
        selectedAccount?.transactions?.filter(
          (transaction: any) =>
            transaction.portfolioCode == selectedPortfolio?.accountCode
        )
      );
      setPortfolioList([selectedPortfolio]);
      setAssets(
        selectedAccount?.holdings?.filter(
          (item: any) =>
            item.assetLiabilityCode === "A" &&
            item.portfolioCode == selectedPortfolio?.accountCode
        )
      );
      setLiabilities(
        selectedAccount?.holdings?.filter(
          (item: any) =>
            item.assetLiabilityCode === "L" &&
            item.portfolioCode == selectedPortfolio?.accountCode
        )
      );
    } else {
      if (selectedAccount) {
        setPortfolioList(selectedAccount?.portfolios);
        setTransactions(selectedAccount?.transactions);
        setAssets(
          selectedAccount?.holdings?.filter(
            (item: any) => item.assetLiabilityCode === "A"
          )
        );
        setLiabilities(
          selectedAccount?.holdings?.filter(
            (item: any) => item.assetLiabilityCode === "L"
          )
        );
        setPendingSubscriptionRequests(
          selectedAccount?.requests?.filter(
            (item: any) =>
              (item.type == RequestsType.SUBSCRIPTION ||
                item.type == RequestsType.WITHDRAW) &&
              item.status.toLowerCase() == Status.Pending
          )
        );
      }
    }
  }, [selectedPortfolio, selectedAccount]);

  useEffect(() => {
    setAssetsTotal(getArraySumission(assets));
  }, [assets]);

  useEffect(() => {
    setLiabilitiesTotal(getArraySumission(liabilities));
  }, [liabilities]);

  useEffect(() => {
    setNetWorth(assetsTotal - Math.abs(liabilitiesTotal));
  }, [assetsTotal, liabilitiesTotal]);

  const getArraySumission = (data: any[]): number => {
    return data?.reduce(
      (accumulator, currentValue) =>
        accumulator + currentValue.reportingMarketAmount,
      0
    );
  };

  return (
    <DashboardContext.Provider
      value={{
        assets,
        liabilities,
        selectedAccount,
        transactions,
        assetsTotal,
        liabilitiesTotal,
        netWorth,
        portfolioList,
        setSelectedAccount,
        selectedPortfolio,
        setSelectedPortfolio,
        fxRates,
        availableCash,
        sumOfPendingCash,
        pendingDepositCash,
        pendingRedemptionCash,
        pendingWithdrawCash,
        pendingSubscriptionRequests,
        loading,
        totalPayableCash,
        selectedAccountTotalAssets,
        selectedAccountTotalLiabilities,
        selectedAccountNetWorth,
        pendingSubscriptionCash,
        setLoading,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
